import React from 'react'
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  Select,
  InputNumber,
  Space,
  message,
  Modal,
  Radio,
  Alert,
} from 'antd'
import {
  ArrowLeftOutlined,
  SaveOutlined,
  UserAddOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { Lead, Discussion, LeadSource, LeadStage } from '@/types'
import { leadStore } from './leadStore'
import { useMasterData } from '@/context/MasterDataContext'
import { NotificationEngine } from '@/utils/notificationEngine'
import { authStore } from '@/context/auth'
import { ApiError } from '@/services/api'
import { generateCode } from '@/utils/codeGenerator'
import MultiImageUploader from '@/components/MultiImageUploader'
import { findSameNameCompanies, standardizeCompanyName } from '@/utils/companyMatch'
import { annualToMonthlyUnits, formatUnitsKWh } from '@/utils/numberFormat'
import {
  findLeadByGst,
  findLeadByHtsc,
  normalizeGst,
  normalizeHtsc,
  isValidGstFormat,
  addDays,
  formatDate,
} from '@/utils/gst'
import './LeadList.css'

const VOLTAGE_OPTIONS = [
  { label: '11 KV', value: '11 KV' },
  { label: '22 KV', value: '22 KV' },
  { label: '33 KV', value: '33 KV' },
  { label: '66 KV', value: '66 KV' },
  { label: '110 KV', value: '110 KV' },
  { label: '220 KV', value: '220 KV' },
]

const DEMAND_UNITS = [
  { label: 'KW', value: 'KW' },
  { label: 'MW', value: 'MW' },
]

const LeadForm: React.FC = () => {
  const [form] = Form.useForm()
  const requiredUnitsWatch = Form.useWatch('requiredUnitsPerAnnum', form)
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditing = !!id
  const [submitting, setSubmitting] = React.useState(false)

  // L-06: Assign To from Employee Master; Type of Power / Mode of Supply from their masters
  const { employees, powerTypes, supplyModes, edcs } = useMasterData()
  // Grouped by role so you can choose a role, then the person to assign
  const salesUserOptions = Object.entries(
    employees
      .filter(u => u.status === 'active')
      .reduce<Record<string, { name: string }[]>>((acc, u) => {
        (acc[u.role] = acc[u.role] || []).push(u)
        return acc
      }, {})
  ).map(([role, list]) => ({
    label: role,
    options: list.map(u => ({ label: u.name, value: u.name })),
  }))

  React.useEffect(() => {
    if (isEditing && id) {
      const existing = leadStore.getLead(id)
      if (existing) {
        form.setFieldsValue({
          ...existing,
        })
      } else {
        message.error('Lead not found')
        navigate('/leads')
      }
    }
  }, [isEditing, id, form, navigate])

  // Same-lead check: HTSC Number and GST Number are the only true unique
  // identifiers a lead has, so a match on either is a hard duplicate and is
  // never allowed through (the backend enforces the same rule — see
  // UNIQUE_KEYS.leads in crudService.js). Company/customer name and mobile
  // number are deliberately NOT treated as duplicates here — two different
  // leads can legitimately share a name or a shared office phone — those are
  // handled by the softer "same company or new?" ask below.
  const findDuplicateLead = (values: any): { lead: Lead; matchedOn: string[] } | undefined => {
    const leads = leadStore.getLeads()
    const excludeId = isEditing ? id : undefined
    const htscDupe = values.htscNumber && !isUnchangedOnEdit('htscNumber', values.htscNumber)
      ? findLeadByHtsc(values.htscNumber, leads, excludeId) : undefined
    const gstDupe = values.gstNumber && !isUnchangedOnEdit('gstNumber', values.gstNumber)
      ? findLeadByGst(values.gstNumber, leads, excludeId) : undefined
    if (!htscDupe && !gstDupe) return undefined

    const lead = htscDupe || gstDupe!
    const matchedOn = [
      htscDupe ? 'HTSC Number' : null,
      gstDupe && gstDupe.id === lead.id ? 'GST Number' : null,
    ].filter((m): m is string => !!m)
    return { lead, matchedOn }
  }

  // ── Real-time company-name / HTSC / GST duplicate detection ───────────
  // HTSC and GST are checked while editing too (excluding this lead itself) —
  // an edit can introduce a duplicate just as easily as a create can.
  const watchedCompanyName = (Form.useWatch('companyName', form) as string | undefined) ?? ''
  const watchedHtscNumber = (Form.useWatch('htscNumber', form) as string | undefined) ?? ''
  const watchedGstNumber = (Form.useWatch('gstNumber', form) as string | undefined) ?? ''
  // Consumer leads may be saved without demand/voltage; Generators still
  // require them, and the voltage field is labelled per lead type.
  const watchedLeadType = Form.useWatch('leadType', form) as 'Consumer' | 'Generator' | undefined
  const isGenerator = watchedLeadType === 'Generator'
  const [companyMatches, setCompanyMatches] = React.useState<Lead[]>([])
  const [htscDuplicateLead, setHtscDuplicateLead] = React.useState<Lead | null>(null)
  const [matchDecision, setMatchDecision] = React.useState<'unresolved' | 'existing' | 'new'>('unresolved')
  const [resolvedForName, setResolvedForName] = React.useState('')
  const [gstDuplicateLead, setGstDuplicateLead] = React.useState<Lead | null>(null)
  const topMatch = companyMatches[0]
  // The lead can be saved with either field blank — there's a 2-day window to
  // fill them in — but never with a value that already belongs to another lead.
  const hasDuplicateBlock = !!htscDuplicateLead || !!gstDuplicateLead

  React.useEffect(() => {
    if (isEditing) return
    const name = watchedCompanyName.trim()
    const timer = setTimeout(() => {
      setCompanyMatches(name ? findSameNameCompanies(name, leadStore.getLeads()) : [])
      if (name !== resolvedForName) setMatchDecision('unresolved')
    }, 300)
    return () => clearTimeout(timer)
  }, [watchedCompanyName, isEditing, resolvedForName])

  // A lead that already carries a legacy collision (data predating this rule)
  // must stay editable on its other fields, so on edit only a *changed* number
  // is checked — the same allowance the backend makes in crudService.update().
  const isUnchangedOnEdit = (field: 'htscNumber' | 'gstNumber', value: string) => {
    if (!isEditing || !id) return false
    const original = leadStore.getLead(id)?.[field] || ''
    const norm = field === 'gstNumber' ? normalizeGst : normalizeHtsc
    return norm(original) === norm(value)
  }

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setHtscDuplicateLead(
        normalizeHtsc(watchedHtscNumber) && !isUnchangedOnEdit('htscNumber', watchedHtscNumber)
          ? findLeadByHtsc(watchedHtscNumber, leadStore.getLeads(), isEditing ? id : undefined) || null
          : null
      )
    }, 250)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedHtscNumber, isEditing, id])

  React.useEffect(() => {
    const timer = setTimeout(() => {
      const gst = normalizeGst(watchedGstNumber)
      setGstDuplicateLead(
        gst.length === 15 && !isUnchangedOnEdit('gstNumber', gst)
          ? findLeadByGst(gst, leadStore.getLeads(), isEditing ? id : undefined) || null
          : null
      )
    }, 250)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedGstNumber, isEditing, id])

  // "Use Existing Company" — log this inquiry against the matched lead
  // instead of creating a new one, and jump straight to it.
  const useExistingCompany = (existing: Lead) => {
    setResolvedForName(watchedCompanyName.trim())
    setMatchDecision('existing')
    mergeIntoExistingLead(existing, form.getFieldsValue(true))
  }

  const continueAsNewCompany = () => {
    setResolvedForName(watchedCompanyName.trim())
    setMatchDecision('new')
  }

  // Approved duplicate: merge data into existing lead and log discussion note.
  const mergeIntoExistingLead = async (existing: Lead, values: any) => {
    const actor = authStore.getUser()?.name
    if (!actor) {
      message.error('Your session looks stale — please refresh the page and sign in again before logging this.')
      return
    }
    try {
      const content = [
        `Inquiry merged into existing lead by ${actor}.`,
        `Contact: ${values.customerName || '-'} — ${values.contactNumber || '-'}${values.email ? ', ' + values.email : ''}`,
        `Address: ${[values.address, values.city, values.pinCode].filter(Boolean).join(', ') || '-'}`,
        `Sanctioned Demand: ${values.sanctionedDemand ?? '-'} ${values.demandUnit || ''}`.trim(),
        `Drawal Voltage: ${values.drawlVoltage || '-'}`,
        values.htscNumber ? `HTSC Number: ${values.htscNumber}` : null,
        values.gstNumber ? `GST Number: ${values.gstNumber}` : null,
        values.edcName ? `EDC: ${values.edcName}` : null,
        `Lead Source: ${values.leadSource || '-'}`,
        values.assignedTo ? `Requested Assign To: ${values.assignedTo}` : null,
      ].filter(Boolean).join('\n')

      const newDisc: Discussion = {
        id: `D${Date.now()}`,
        type: 'note',
        content,
        createdBy: actor,
        createdAt: new Date(),
      }

      const patch: Partial<Lead> = {
        id: existing.id,
        discussionHistory: [newDisc, ...(existing.discussionHistory || [])],
      }

      if (!existing.htscNumber && values.htscNumber) patch.htscNumber = values.htscNumber
      if (!existing.gstNumber && values.gstNumber) patch.gstNumber = normalizeGst(values.gstNumber)
      if (!existing.edcName && values.edcName) patch.edcName = values.edcName
      if (!existing.email && values.email) patch.email = values.email
      if (!existing.contactNumber && values.contactNumber) patch.contactNumber = values.contactNumber

      await leadStore.saveLeadAndConfirm(patch)

      message.success(`Merged into existing lead "${existing.companyName}" successfully!`)
      navigate(`/leads/${existing.id}`)
    } catch {
      message.error('Failed to merge into the existing lead. Please try again.')
    }
  }

  const createNewLead = async (values: any) => {
    let leadId = id
    if (!isEditing) {
      try {
        leadId = await generateCode('leads')
      } catch (err) {
        leadId = `L${Date.now().toString(36).toUpperCase()}${Math.random().toString(36).slice(2, 5).toUpperCase()}`
      }
    }
    const payload = {
      ...values,
      id: leadId,
    }
    const oldLead = isEditing && id ? leadStore.getLead(id) : null

    setSubmitting(true)
    let savedLead: Lead
    try {
      // Wait for the server to actually accept the write before telling the
      // user it saved — the optimistic cache update alone isn't confirmation.
      savedLead = await leadStore.saveLeadAndConfirm(payload)
    } catch (err) {
      setSubmitting(false)
      message.error(err instanceof ApiError && err.message ? err.message : 'Failed to save lead. Please try again.')
      return
    }
    setSubmitting(false)

    // NT-02: Trigger event on new lead assignment or when assignee changes
    const isNew = !isEditing
    const assigneeChanged = oldLead && oldLead.assignedTo !== values.assignedTo
    if ((isNew || assigneeChanged) && values.assignedTo) {
      NotificationEngine.trigger({
        event: 'LEAD_ASSIGNED',
        title: `New Lead Assigned: ${values.companyName}`,
        message: `Lead ${savedLead.id} for ${values.companyName} has been assigned to ${values.assignedTo}. Contact: ${values.customerName} (${values.contactNumber})`,
        recipient: values.assignedTo,
      })
    }

    message.success(`Lead ${isEditing ? 'updated' : 'created'} successfully!`)
    navigate('/leads')
  }

  // Company-name collision: ask "same company, or a genuinely new one?" before
  // saving, so two records for one company aren't created by accident. Only an
  // identical name prompts — a company that merely shares a word ("arun
  // textile" vs "Textile Valley") is a different company and never asks.
  const confirmSameOrNewCompany = (values: any): Promise<'existing' | 'new' | 'cancelled'> => {
    const exact = companyMatches[0]
    if (isEditing || !exact || matchDecision !== 'unresolved') return Promise.resolve('new')

    return new Promise((resolve) => {
      Modal.confirm({
        title: 'A lead with this company name already exists',
        icon: <ExclamationCircleOutlined />,
        width: 520,
        content: (
          <div>
            <p style={{ margin: '8px 0' }}>
              <strong>{exact.companyName}</strong>
              {exact.htscNumber ? ` | HTSC: ${exact.htscNumber}` : ''}
              {exact.gstNumber ? ` | GST: ${exact.gstNumber}` : ''}
              <br />
              <span style={{ color: '#888' }}>
                Stage: {exact.stage} · Contact: {exact.customerName || '-'}
              </span>
            </p>
            <p>Is this the <strong>same company</strong>, or a <strong>new</strong> one?</p>
          </div>
        ),
        okText: 'Same Company — Add to Existing Lead',
        cancelText: 'New Company — Create Separately',
        onOk: () => {
          setResolvedForName(String(values.companyName || '').trim())
          setMatchDecision('existing')
          resolve('existing')
        },
        onCancel: () => {
          setResolvedForName(String(values.companyName || '').trim())
          setMatchDecision('new')
          resolve('new')
        },
      })
    }).then(async (decision) => {
      if (decision === 'existing') {
        await mergeIntoExistingLead(exact, values)
        return 'existing'
      }
      return 'new'
    }) as Promise<'existing' | 'new' | 'cancelled'>
  }

  const onFinish = async (values: any) => {
    // Standardize Company Name before duplicate check and save
    if (values.companyName) {
      values.companyName = standardizeCompanyName(values.companyName)
    }

    if (await confirmSameOrNewCompany(values) === 'existing') return

    // HTSC / GST are unique per lead: a match on either is a hard duplicate,
    // on create and on edit alike. The only way forward is to fold this
    // inquiry into the lead that already owns that number.
    const dup = findDuplicateLead(values)
    if (dup && isEditing) {
      message.error(
        `${dup.matchedOn.join(' & ')} already belongs to lead "${dup.lead.companyName}". These numbers must be unique — please correct them before saving.`
      )
      return
    }
    if (dup) {
      Modal.confirm({
        title: 'Duplicate Lead Detected',
        icon: <ExclamationCircleOutlined />,
        width: 480,
        content: (
          <div>
            <p>An existing lead already has the same <strong>{dup.matchedOn.join(', ')}</strong>:</p>
            <p style={{ margin: '8px 0' }}>
              <strong>{dup.lead.companyName}</strong>
              {dup.lead.htscNumber ? ` | HTSC: ${dup.lead.htscNumber}` : ''}
              {dup.lead.gstNumber ? ` | GST: ${dup.lead.gstNumber}` : ''}
              <br />
              <span style={{ color: '#888' }}>
                Stage: {dup.lead.stage} · Added: {dup.lead.createdAt ? new Date(dup.lead.createdAt).toLocaleDateString() : ''}
              </span>
            </p>
            <p>Can I merge this lead into the existing record?</p>
          </div>
        ),
        okText: 'Yes, Merge It',
        cancelText: 'Cancel',
        onOk: () => mergeIntoExistingLead(dup.lead, values),
      })
      return
    }

    // gstStatus / gstVerifyDueDate / isDeactive are derived from the saved
    // record by computeLeadDeactiveState (applied in leadStore), so the form
    // only has to hand over clean values — no status bookkeeping here.
    const payload = {
      ...values,
      htscNumber: values.htscNumber ? String(values.htscNumber).trim() : undefined,
      gstNumber: values.gstNumber ? normalizeGst(values.gstNumber) : undefined,
    }
    await createNewLead(payload)
  }

  return (
    <div className="lead-form-container">
      {/* Header */}
      <div className="lf-header">
        <div className="lf-header-left">
          <Button
            icon={<ArrowLeftOutlined />}
            type="text"
            onClick={() => navigate('/leads')}
            className="lf-back-btn"
          />
          <div>
            <h2>{isEditing ? 'Edit Lead' : 'Add New Lead'}</h2>
            <p className="lf-header-sub">
              {isEditing
                ? 'Update lead information'
                : 'Capture a new prospect — all fields marked * are required'}
            </p>
          </div>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        initialValues={{ demandUnit: 'KW', stage: LeadStage.COLD }}
        scrollToFirstError
      >
        {/* ── Section 1: Lead Classification ───────────── */}
        <Card className="lf-section-card">
          <div className="lf-section-title">
            <span className="lf-section-num">1</span>
            Lead Classification
          </div>
          <Row gutter={[20, 0]}>
            <Col xs={24} sm={12}>
              {/* BRD: Lead Type = Consumer / Generator */}
              <Form.Item
                name="leadType"
                label="Lead Type"
                rules={[{ required: true, message: 'Lead type is required' }]}
              >
                <Radio.Group className="lf-lead-type-group" size="large">
                  <Radio.Button value="Consumer">👤 Consumer</Radio.Button>
                  <Radio.Button value="Generator">⚡ Generator</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              {/* L-04: Lead source — manual entry by user */}
              <Form.Item
                name="leadSource"
                label="Lead Source"
                rules={[{ required: true, message: 'Lead source is required' }]}
              >
                <Select
                  placeholder="How did this lead come in?"
                  size="large"
                  options={Object.values(LeadSource).map((s) => ({
                    label: s,
                    value: s,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              {/* L-06: Manual assignment */}
              <Form.Item
                name="assignedTo"
                label="Assign To (Employee)"
              >
                <Select
                  placeholder="Select employee (optional)"
                  allowClear
                  size="large"
                  options={salesUserOptions}
                  suffixIcon={<UserAddOutlined />}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              {/* L-02: Initial stage */}
              <Form.Item
                name="stage"
                label="Initial Stage"
              >
                <Select
                  size="large"
                  options={[
                    { label: '🔴 Hot', value: LeadStage.HOT },
                    { label: '🟡 Warm', value: LeadStage.WARM },
                    { label: '🔵 Cold', value: LeadStage.COLD },
                    { label: '⚫ Lost', value: LeadStage.LOST },
                    { label: '🏆 Won', value: LeadStage.WON },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ── Section 2: Company Info ───────────────────── */}
        <Card className="lf-section-card">
          <div className="lf-section-title">
            <span className="lf-section-num">2</span>
            Company Information
          </div>
          <Row gutter={[20, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="companyName"
                label="Company Name"
                rules={[{ required: true, message: 'Company name is required' }]}
                style={{ marginBottom: companyMatches.length && matchDecision === 'unresolved' ? 4 : undefined }}
              >
                <Input
                  placeholder="e.g. ABC Textiles P Ltd"
                  size="large"
                  onBlur={(e) => {
                    const formatted = standardizeCompanyName(e.target.value)
                    if (formatted && formatted !== e.target.value) {
                      form.setFieldValue('companyName', formatted)
                    }
                  }}
                />
              </Form.Item>

              {/* Duplicate-company ask — anchored to this field, and raised only
                  when a lead already carries THIS EXACT name. A company that
                  merely shares a word is a different company and never appears
                  here (see findSameNameCompanies). More than one lead can
                  legitimately hold the same name, so all of them are listed. */}
              {companyMatches.length > 0 && matchDecision === 'unresolved' && (
                <div className="lf-company-suggestion">
                  <div className="lf-company-suggestion-text">
                    <ExclamationCircleOutlined style={{ color: '#D97706', marginRight: 6 }} />
                    {companyMatches.length === 1
                      ? <>A lead already exists under this name: <strong>"{companyMatches[0].companyName}"</strong>. Is this the same company?</>
                      : <>{companyMatches.length} leads already exist under this name. Is this one of them?</>}
                  </div>
                  <div className="lf-company-suggestion-list">
                    {companyMatches.slice(0, 5).map((m) => (
                      <div key={m.id} className="lf-company-suggestion-item">
                        <span>
                          {m.companyName}
                          <span style={{ color: '#888', marginLeft: 8, fontSize: 12 }}>
                            {m.customerName || '-'}{m.city ? ` · ${m.city}` : ''}
                          </span>
                        </span>
                        <Button size="small" type="primary" onClick={() => useExistingCompany(m)}>
                          Use This
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button size="small" style={{ marginTop: 8 }} onClick={continueAsNewCompany}>
                    Not the same — Continue as New Company
                  </Button>
                </div>
              )}
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="customerName"
                label="Customer Name (Contact Person)"
                rules={[{ required: true, message: 'Customer name is required' }]}
              >
                <Input placeholder="e.g. Rajesh Kumar" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="contactNumber"
                label="Contact Number"
                rules={[
                  { required: true, message: 'Contact number is required' },
                  { pattern: /^[0-9]{10}$/, message: 'Enter a valid 10-digit number' },
                ]}
              >
                <Input placeholder="+91 98765 43210" size="large" maxLength={10} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { required: true, message: 'Email is required' },
                  { type: 'email', message: 'Enter a valid email' },
                ]}
              >
                <Input placeholder="name@company.com" size="large" />
              </Form.Item>
            </Col>
          </Row>

          {matchDecision === 'new' && topMatch && (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 12 }}
              message="Saving as a new company"
              description={`Marked as separate from the existing "${topMatch.companyName}". Add the GST Number under Technical Details to confirm it really is a different registration.`}
            />
          )}
        </Card>

        {/* ── Section 3: Address Information ────────────── */}
        <Card className="lf-section-card">
          <div className="lf-section-title">
            <span className="lf-section-num">3</span>
            Address Information
          </div>
          <Row gutter={[20, 0]}>
            <Col xs={24}>
              <Form.Item
                name="address"
                label="Street Address"
              >
                <Input placeholder="Street / Area / Landmark" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="city"
                label="City"
              >
                <Input placeholder="e.g. Madurai" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="pinCode"
                label="PIN Code"
                rules={[
                  { pattern: /^[0-9]{6}$/, message: 'Enter a valid 6-digit PIN' },
                ]}
              >
                <Input placeholder="e.g. 625001" size="large" maxLength={6} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ── Section 4: Technical Details ──────────────── */}
        <Card className="lf-section-card">
          <div className="lf-section-title">
            <span className="lf-section-num">4</span>
            Technical Details
          </div>
          <Row gutter={[20, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="htscNumber"
                label="HTSC Number"
                tooltip="High Tension Service Connection number — can be filled in within 2 days of creating the lead"
                validateStatus={htscDuplicateLead ? 'error' : undefined}
                help={htscDuplicateLead ? 'This HTSC Number already belongs to another lead' : undefined}
              >
                <Input placeholder="e.g. 12345" size="large" />
              </Form.Item>
              {htscDuplicateLead && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={`HTSC Number ${htscDuplicateLead.htscNumber} already belongs to "${htscDuplicateLead.companyName}". HTSC Numbers can't be duplicated.`}
                  action={
                    !isEditing && (
                      <Button size="small" danger onClick={() => mergeIntoExistingLead(htscDuplicateLead, form.getFieldsValue(true))}>
                        Use Existing Lead
                      </Button>
                    )
                  }
                />
              )}
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="gstNumber"
                label="GST Number"
                tooltip="15-character GSTIN — can be filled in within 2 days of creating the lead"
                normalize={(v) => (v ? String(v).toUpperCase() : v)}
                validateStatus={gstDuplicateLead ? 'error' : undefined}
                help={gstDuplicateLead ? 'This GST Number already belongs to another lead' : undefined}
                rules={[
                  {
                    validator: async (_, value) =>
                      !value || isValidGstFormat(value)
                        ? Promise.resolve()
                        : Promise.reject(new Error('Enter a valid 15-character GST number')),
                  },
                ]}
              >
                <Input
                  placeholder="e.g. 33AAACT1234A1Z5"
                  size="large"
                  style={{ fontFamily: 'monospace' }}
                  maxLength={15}
                />
              </Form.Item>
              {gstDuplicateLead && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={`GST Number ${gstDuplicateLead.gstNumber} already belongs to "${gstDuplicateLead.companyName}". GST Numbers can't be duplicated.`}
                  action={
                    !isEditing && (
                      <Button size="small" danger onClick={() => useExistingCompany(gstDuplicateLead)}>
                        Use Existing Company
                      </Button>
                    )
                  }
                />
              )}
            </Col>
            <Col xs={24}>
              {/* The 2-day update window. Leaving HTSC/GST blank is allowed and
                  never blocks the lead — it only schedules the reminder and,
                  once the window lapses, the Deactive mark. */}
              {(!watchedHtscNumber.trim() || !watchedGstNumber.trim()) && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={`${[!watchedHtscNumber.trim() ? 'HTSC Number' : null, !watchedGstNumber.trim() ? 'GST Number' : null].filter(Boolean).join(' & ')} not entered`}
                  description={`You can save the lead without ${!watchedHtscNumber.trim() && !watchedGstNumber.trim() ? 'them' : 'it'} — nothing is blocked. A reminder will be raised to update ${!watchedHtscNumber.trim() && !watchedGstNumber.trim() ? 'them' : 'it'} within 2 days of the lead being created${isEditing ? '' : ` (by ${formatDate(addDays(new Date(), 2))})`}.`}
                />
              )}
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="edcName"
                label="EDC Name"
                tooltip="Electricity Distribution Circle — from EDC Master"
              >
                <Select
                  placeholder="Select EDC"
                  size="large"
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  options={edcs
                    .filter(e => e.status === 'active')
                    .map(e => ({ value: e.name, label: e.place ? `${e.name} — ${e.place}` : e.name }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              {/* Item 4: Sanctioned Demand is optional for Consumer leads */}
              <Form.Item label="Sanctioned Demand">
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item
                    name="sanctionedDemand"
                    noStyle
                    rules={[
                      { type: 'number', min: 1, message: 'Must be > 0' },
                    ]}
                  >
                    <InputNumber
                      placeholder="e.g. 500"
                      size="large"
                      min={0}
                      style={{ width: 'calc(100% - 90px)' }}
                    />
                  </Form.Item>
                  <Form.Item name="demandUnit" noStyle>
                    <Select
                      size="large"
                      options={DEMAND_UNITS}
                      style={{ width: 90 }}
                    />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              {/* Item 5: Dynamic Injection Voltage vs Drawal Voltage label */}
              <Form.Item
                name="drawlVoltage"
                label={isGenerator ? 'Injection Voltage' : 'Drawal Voltage'}
              >
                <Select
                  placeholder="Select voltage level"
                  size="large"
                  allowClear
                  options={VOLTAGE_OPTIONS}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>



        {/* ── Section 5: Power Requirement ──────────────── */}
        <Card className="lf-section-card">
          <div className="lf-section-title">
            <span className="lf-section-num">5</span>
            Power Requirement
          </div>
          <Row gutter={[20, 0]}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="typeOfPower"
                label="Type of Power"
                tooltip="From Masters → Type of Power (e.g. Solar / Wind / Hybrid)"
              >
                <Select
                  placeholder="Select type of power"
                  size="large"
                  allowClear
                  options={powerTypes
                    .filter((p) => p.status === 'active')
                    .map((p) => ({ label: p.name, value: p.name }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="modeOfSupply"
                label="Mode of Supply"
                tooltip="From Masters → Mode of Supply (e.g. Third Party / Group Captive)"
              >
                <Select
                  placeholder="Select mode of supply"
                  size="large"
                  allowClear
                  options={supplyModes
                    .filter((m) => m.status === 'active')
                    .map((m) => ({ label: m.name, value: m.name }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="requiredUnitsPerAnnum"
                label="Supply Units Per Annum (kWh)"
                tooltip="Expected annual consumption / generation in units (kWh)"
                extra={`≈ ${formatUnitsKWh(annualToMonthlyUnits(requiredUnitsWatch))}/month`}
              >
                <InputNumber
                  placeholder="e.g. 1200000"
                  size="large"
                  min={0}
                  style={{ width: '100%' }}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(v) => Number((v || '').replace(/,/g, '')) as any}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ── Section 6: Attachments ────────────────────── */}
        <Card className="lf-section-card">
          <div className="lf-section-title">
            <span className="lf-section-num">6</span>
            Attachments
          </div>
          <Form.Item
            name="attachments"
            label="Photos / Documents"
            tooltip="Stored in cloud object storage (R2), not on the server disk"
          >
            <MultiImageUploader folder="leads" />
          </Form.Item>
        </Card>

        {/* ── Actions ───────────────────────────────────── */}
        <div className="lf-actions">
          <Button size="large" onClick={() => navigate('/leads')} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            icon={<SaveOutlined />}
            loading={submitting}
            disabled={hasDuplicateBlock}
          >
            {isEditing ? 'Update Lead' : 'Create Lead'}
          </Button>
        </div>
      </Form>
    </div>
  )
}

export default LeadForm
